import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { constructDynamicFilters } from '@/hooks/useDocumentGrid'
import { useGetDocumentsCount } from '@/service-library/hooks/documents'
import { partitionWorkflowStateIdsByDoneness } from '@/utils/workflow-states'
import { useDocumentFiltersContext } from '@/components/document-filters/DocumentFiltersProvider'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'

// Poll the fast (Intake / In Progress / untyped) states frequently; poll the
// Done states — which are slow and high-volume but rarely change — much less
// often. See sc-24728.
const FAST_STATES_REFETCH_INTERVAL = 2000
const DONE_STATES_REFETCH_INTERVAL = 15000

const GROUP_BY_FILTERS = {
  '~group-by':
    'workflow_states__id,document_workflow_states__processing_started_at:isnull',
  '~group-by-use-distinct': 'True',
}

/**
 * Restrict `stateIds` to those already selected by an incoming
 * `workflow_states__id__in` filter, if any. The Workflow State grid column
 * filter (always `__in` mode) surfaces here via `constructDynamicFilters`, so
 * without this intersection the per-bucket `workflow_states__id__in` we add
 * below would clobber the user's own state filter. When no such filter is
 * present, the bucket's ids apply as-is.
 */
function restrictToSelectedStateIds(
  stateIds: string[],
  existingStateIdInFilter?: string,
) {
  if (!existingStateIdInFilter) return stateIds
  const selected = new Set(existingStateIdInFilter.split(','))
  return stateIds.filter((id) => selected.has(id))
}

export default function useDocumentCountPerWorkflowState() {
  const { isApplyingFilters, tableCuratedFilters } = useDocumentFiltersContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const { noFlags, flagCodes, filtering, orgIds, teamIds, tagIds } =
    tableCuratedFilters

  // The document overlay mirrors its open/closed state as the `document_id`
  // search param (see DashboardTable's `documentIdState`). These badge counts
  // are hidden behind the overlay while it's open, so there's no reason to
  // keep polling them — see sc-25207.
  const [searchParams] = useSearchParams()
  const isOverlayOpen = searchParams.has('document_id')

  const { doneStateIds, fastStateIds } = useMemo(
    () =>
      partitionWorkflowStateIdsByDoneness(
        selectedWorkflow?.workflow_states ?? [],
      ),
    [selectedWorkflow?.workflow_states],
  )

  // MARK: Unfiltered counts (always polling, unless the overlay is open) —
  // split by state doneness.
  const unfilteredEnabled = !!selectedWorkflow?.id && !isOverlayOpen
  const { groupedCounts: fastGroupedCounts } = useGetDocumentsCount({
    filters: {
      ...GROUP_BY_FILTERS,
      workflow_states__id__in: fastStateIds.join(),
    },
    refetchInterval: isOverlayOpen ? false : FAST_STATES_REFETCH_INTERVAL,
    enabled: unfilteredEnabled && fastStateIds.length > 0,
  })

  const { groupedCounts: doneGroupedCounts } = useGetDocumentsCount({
    filters: {
      ...GROUP_BY_FILTERS,
      workflow_states__id__in: doneStateIds.join(),
    },
    refetchInterval: isOverlayOpen ? false : DONE_STATES_REFETCH_INTERVAL,
    enabled: unfilteredEnabled && doneStateIds.length > 0,
  })

  const groupedCounts = useMemo(
    () => [...fastGroupedCounts, ...doneGroupedCounts],
    [fastGroupedCounts, doneGroupedCounts],
  )

  // MARK: Filtered counts (only while filters are applied) — split the same
  // way, intersecting each bucket with any incoming state filter.
  const dynamicFilters = useMemo(
    () =>
      constructDynamicFilters(
        'document',
        filtering || [],
        undefined,
        selectedWorkflow?.id,
      ),
    [filtering, selectedWorkflow?.id],
  )

  const curatedFilters = useMemo(
    () => ({
      ...GROUP_BY_FILTERS,
      ...dynamicFilters,
      self_and_descendants_of_owner_org_id__in: orgIds
        ? orgIds.join()
        : undefined,
      team_id__in: teamIds?.join(),
      any_flag__flag_level__in: flagCodes?.join(),
      no_flags: !flagCodes && noFlags ? 'true' : undefined,
      doc_project_tags__project_tag_id__in: tagIds?.join(),
    }),
    [dynamicFilters, orgIds, teamIds, flagCodes, noFlags, tagIds],
  )

  const filteredFastStateIds = restrictToSelectedStateIds(
    fastStateIds,
    dynamicFilters.workflow_states__id__in,
  )
  const filteredDoneStateIds = restrictToSelectedStateIds(
    doneStateIds,
    dynamicFilters.workflow_states__id__in,
  )

  const filteredEnabled = isApplyingFilters && !isOverlayOpen
  const { groupedCounts: filteredFastGroupedCounts } = useGetDocumentsCount({
    filters: {
      ...curatedFilters,
      workflow_states__id__in: filteredFastStateIds.join(),
    },
    refetchInterval: filteredEnabled ? FAST_STATES_REFETCH_INTERVAL : false,
    enabled: filteredEnabled && filteredFastStateIds.length > 0,
  })

  const { groupedCounts: filteredDoneGroupedCounts } = useGetDocumentsCount({
    filters: {
      ...curatedFilters,
      workflow_states__id__in: filteredDoneStateIds.join(),
    },
    refetchInterval: filteredEnabled ? DONE_STATES_REFETCH_INTERVAL : false,
    enabled: filteredEnabled && filteredDoneStateIds.length > 0,
  })

  const filteredGroupedCounts = useMemo(
    () => [...filteredFastGroupedCounts, ...filteredDoneGroupedCounts],
    [filteredFastGroupedCounts, filteredDoneGroupedCounts],
  )

  const { idleCountByStateId, inFlightCountByStateId } = useMemo(() => {
    return groupedCounts.reduce<Record<string, Record<string, number>>>(
      (acc, val) => {
        if (val.document_workflow_states__processing_started_at__isnull) {
          acc.idleCountByStateId[val.workflow_states__id] = val.count
        } else {
          acc.inFlightCountByStateId[val.workflow_states__id] = val.count
        }
        return acc
      },
      { idleCountByStateId: {}, inFlightCountByStateId: {} },
    )
  }, [groupedCounts])

  const filteredIdleCountByStateId = useMemo(() => {
    return filteredGroupedCounts.reduce<Record<string, number>>((acc, val) => {
      if (val.document_workflow_states__processing_started_at__isnull) {
        acc[val.workflow_states__id] = val.count
      }
      return acc
    }, {})
  }, [filteredGroupedCounts])

  return useMemo(
    () => ({
      filteredIdleCountByStateId,
      idleCountByStateId,
      inFlightCountByStateId,
    }),
    [filteredIdleCountByStateId, idleCountByStateId, inFlightCountByStateId],
  )
}
